import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  getPendingChanges, getPendingChangesCount, approvePendingChange, 
  rejectPendingChange, getActivityLog, deleteActivityLogs, deleteActivityLogEntry,
} from '@/services/api';
import { 
  CheckCircle, XCircle, Clock, AlertTriangle, Eye, 
  Filter, RefreshCw, FileText, User, Calendar,
  Activity, ChevronDown, ChevronUp, Trash2, Mail
} from 'lucide-react';

// Pending Changes Page
export const PendingChangesPage = () => {
  const { isMasterAdmin } = useAuth();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedChange, setSelectedChange] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [changesData, countData] = await Promise.all([
        getPendingChanges(statusFilter),
        getPendingChangesCount()
      ]);
      setChanges(changesData);
      setPendingCount(countData.pending_count);
    } catch (error) {
      toast.error('Failed to load pending changes');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (changeId) => {
    setProcessing(true);
    try {
      await approvePendingChange(changeId, reviewNotes);
      toast.success('Change approved and applied');
      setShowDetailDialog(false);
      setReviewNotes('');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to approve change');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (changeId) => {
    if (!reviewNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      await rejectPendingChange(changeId, reviewNotes);
      toast.success('Change rejected');
      setShowDetailDialog(false);
      setReviewNotes('');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to reject change');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'bg-blue-100 text-blue-800';
      case 'update': return 'bg-purple-100 text-purple-800';
      case 'delete': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const openDetail = (change) => {
    setSelectedChange(change);
    setReviewNotes('');
    setShowDetailDialog(true);
  };

  if (!isMasterAdmin) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">Only the Master Admin can view pending changes.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="pending-changes-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Pending Changes</h1>
            <p className="text-gray-500">Review and approve changes made by users</p>
          </div>
          <div className="flex gap-3 items-center">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            {pendingCount > 0 && (
              <Button 
                variant="outline" 
                data-testid="notify-email-btn"
                onClick={() => {
                  const pending = changes.filter(c => c.status === 'pending');
                  const summary = pending.slice(0, 5).map(c => `- ${c.user_name}: ${c.action} ${c.resource_type}${c.resource_name ? ` (${c.resource_name})` : ''}`).join('\n');
                  const body = `Dear Admin,\n\nThere are ${pendingCount} change(s) awaiting your review on KaizenKanri:\n\n${summary}${pendingCount > 5 ? `\n... and ${pendingCount - 5} more` : ''}\n\nPlease log in to review and approve/reject these changes.\n\nKaizenKanri Platform`;
                  window.open(`mailto:dpetrov@kaizenkanri.nl?subject=${encodeURIComponent(`[KaizenKanri] ${pendingCount} Pending Changes Await Review`)}&body=${encodeURIComponent(body)}`);
                }}
              >
                <Mail className="w-4 h-4 mr-2" /> Notify via Email
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{changes.filter(c => c.status === 'approved').length}</p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{changes.filter(c => c.status === 'rejected').length}</p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{changes.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Changes List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : changes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-4" />
              <p className="text-gray-500">No {statusFilter !== 'all' ? statusFilter : ''} changes found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {changes.map(change => (
              <Card 
                key={change.change_id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openDetail(change)}
                data-testid={`change-card-${change.change_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getStatusColor(change.status)}>
                          {change.status}
                        </Badge>
                        <Badge className={getActionColor(change.action)}>
                          {change.action}
                        </Badge>
                        <Badge variant="outline">{change.resource_type}</Badge>
                      </div>
                      <h3 className="font-semibold">
                        {change.action.charAt(0).toUpperCase() + change.action.slice(1)} {change.resource_type}
                        {change.resource_name ? `: ${change.resource_name}` : ''}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {change.user_name || 'Unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(change.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4 mr-1" /> Review
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review Change Request</DialogTitle>
              <DialogDescription>
                Review the details and approve or reject this change
              </DialogDescription>
            </DialogHeader>
            {selectedChange && (
              <div className="space-y-4 mt-4">
                {/* Change Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Requested By</Label>
                    <p className="font-medium">{selectedChange.user_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedChange.user_email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Submitted</Label>
                    <p className="font-medium">{new Date(selectedChange.created_at).toLocaleString()}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Badge className={getActionColor(selectedChange.action)}>
                    {selectedChange.action}
                  </Badge>
                  <Badge variant="outline">{selectedChange.resource_type}</Badge>
                  <Badge className={getStatusColor(selectedChange.status)}>
                    {selectedChange.status}
                  </Badge>
                </div>

                {selectedChange.resource_name && (
                  <div>
                    <Label className="text-muted-foreground">Resource Name</Label>
                    <p className="font-medium">{selectedChange.resource_name}</p>
                  </div>
                )}

                {/* Data Preview */}
                <div>
                  <Label className="text-muted-foreground">Change Data</Label>
                  <pre className="mt-2 p-4 bg-gray-50 rounded-lg text-sm overflow-x-auto max-h-60">
                    {JSON.stringify(selectedChange.data, null, 2)}
                  </pre>
                </div>

                {/* Original Data (for updates) */}
                {selectedChange.original_data && (
                  <div>
                    <Label className="text-muted-foreground">Original Data</Label>
                    <pre className="mt-2 p-4 bg-gray-50 rounded-lg text-sm overflow-x-auto max-h-60">
                      {JSON.stringify(selectedChange.original_data, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Review Notes */}
                {selectedChange.status === 'pending' ? (
                  <div>
                    <Label>Review Notes</Label>
                    <Textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add notes (required for rejection)"
                      rows={3}
                      data-testid="review-notes"
                    />
                  </div>
                ) : (
                  selectedChange.review_notes && (
                    <div>
                      <Label className="text-muted-foreground">Review Notes</Label>
                      <p className="mt-1 p-3 bg-gray-50 rounded-lg">{selectedChange.review_notes}</p>
                      {selectedChange.reviewed_at && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Reviewed on {new Date(selectedChange.reviewed_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )
                )}

                {/* Action Buttons */}
                {selectedChange.status === 'pending' && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button 
                      onClick={() => handleApprove(selectedChange.change_id)}
                      disabled={processing}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve & Apply
                    </Button>
                    <Button 
                      onClick={() => handleReject(selectedChange.change_id)}
                      disabled={processing}
                      variant="destructive"
                      className="flex-1"
                      data-testid="reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Activity Log Page
export const ActivityLogPage = () => {
  const { isMasterAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, [userFilter, actionFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await getActivityLog(userFilter || null, actionFilter || null);
      setLogs(data);
    } catch (error) {
      toast.error('Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to delete all activity logs? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      const response = await deleteActivityLogs();
      toast.success(response.message || 'Activity logs deleted');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete activity logs');
    } finally {
      setDeleting(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <span className="text-green-500">+</span>;
      case 'update': return <span className="text-blue-500">~</span>;
      case 'delete': return <span className="text-red-500">-</span>;
      case 'login': return <span className="text-purple-500">&rarr;</span>;
      default: return <span className="text-gray-500">*</span>;
    }
  };

  if (!isMasterAdmin) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">Only the Master Admin can view the activity log.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="activity-log-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Activity Log</h1>
            <p className="text-gray-500">Monitor all user actions in the system</p>
          </div>
          <div className="flex gap-3 items-center">
            <Input
              placeholder="Filter by user..."
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="w-48"
            />
            <Select value={actionFilter || 'all'} onValueChange={(v) => setActionFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearLogs}
              disabled={deleting || logs.length === 0}
              data-testid="clear-logs-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Clear All
            </Button>
          </div>
        </div>

        {/* Logs List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : logs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Activity className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No activity logs found</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {logs.map(log => (
                  <div 
                    key={log.log_id} 
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedLog(expandedLog === log.log_id ? null : log.log_id)}
                    data-testid={`log-item-${log.log_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-lg font-mono">
                          {getActionIcon(log.action)}
                        </div>
                        <div>
                          <p className="font-medium">
                            <span className="text-primary">{log.user_name}</span>
                            {' '}
                            <span className="text-muted-foreground">{log.action}</span>
                            {' '}
                            <span>{log.resource_type}</span>
                            {log.resource_name && (
                              <span className="text-muted-foreground">: {log.resource_name}</span>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {log.user_email} • {new Date(log.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {expandedLog === log.log_id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                        {/* Per-entry scrub button — Master Admin only.
                            Confirms before firing because the deletion
                            is irreversible (the row is gone for good,
                            though the deletion itself is logged). */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          onClick={async (ev) => {
                            ev.stopPropagation();
                            if (!window.confirm('Delete this activity-log entry? This action is permanent.')) return;
                            try {
                              await deleteActivityLogEntry(log.log_id);
                              setLogs(prev => prev.filter(l => l.log_id !== log.log_id));
                              toast.success('Log entry deleted');
                            } catch (e) {
                              toast.error(e?.message || 'Could not delete entry');
                            }
                          }}
                          data-testid={`log-delete-${log.log_id}`}
                          title="Delete this log entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedLog === log.log_id && (
                      <div className="mt-4 pl-12 space-y-2">
                        {log.details && (
                          <p className="text-sm"><strong>Details:</strong> {log.details}</p>
                        )}
                        {log.ip_address && (
                          <p className="text-sm text-muted-foreground">IP: {log.ip_address}</p>
                        )}
                        {log.changes && (
                          <div>
                            <strong className="text-sm">Changes:</strong>
                            <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                              {JSON.stringify(log.changes, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PendingChangesPage;
